import { Button, Menu, MenuItem } from '@affine/component';
import { Loading } from '@affine/component/ui/loading';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { GraphQLService } from '@affine/core/modules/cloud';
import type { DocReferenceInfo } from '@affine/core/modules/peek-view/entities/peek-view';
import { DocPeekPreview } from '@affine/core/modules/peek-view/view/doc-preview';
import { MailMessagePreview } from '@affine/core/modules/streamlain/mail/mail-message-preview';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  type MailMessageBodyQuery,
  mailMessageBodyQuery,
} from '@affine/graphql';
import { type I18nString, i18nTime, isI18nString, useI18n } from '@affine/i18n';
import type { SearchSourceType } from '@affine/reader';
import {
  ArrowDownSmallIcon,
  ArrowLeftSmallIcon,
  CloseIcon,
  SearchIcon,
} from '@blocksuite/icons/rc';
import * as Dialog from '@radix-ui/react-dialog';
import {
  FrameworkScope,
  useFramework,
  useLiveData,
  useService,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';

import { DocsService } from '../../doc';
import { DocDisplayMetaService } from '../../doc-display-meta';
import { DocsSearchService } from '../../docs-search';
import {
  buildDocsSearchNavigation,
  createDocsSearchNavigationResolvers,
  type DocsSearchNavigablePayload,
  type DocsSearchNavigation,
  openDocsSearchNavigation,
} from '../../docs-search/services/source-navigation';
import { NavigationPanelRevealService } from '../../navigation-panel';
import { WorkbenchService } from '../../workbench';
import { DocsQuickSearchSession } from '../impls/docs';
import {
  type DocsBlockPayload,
  type DocsPayload,
  getDocsPayloadSourceTypes,
} from '../impls/docs-pdf-search';
import { SEARCH_DEGRADED_STATUS } from '../impls/docs-search-status';
import { type MailPayload, MailQuickSearchSession } from '../impls/mail';
import { RecentDocsQuickSearchSession } from '../impls/recent-docs';
import type { QuickSearchItem } from '../types/item';
import * as styles from './advanced-search-dialog.css';
import { groupQuickSearchItems } from './group-items';
import { HighlightText } from './highlight-text';

type SourceFilter = 'all' | SearchSourceType | 'mail';
type TimeFilter = 'all' | 'day' | 'week' | 'month';

// All source types stay visible as chips: scoping a search to PDFs, images or
// attachments is a primary intent, not something to hide behind a "more" menu.
// The dialog grows wider/taller instead.
const SOURCE_FILTERS: SourceFilter[] = [
  'all',
  'doc',
  'text',
  'database',
  'mail',
  'edgeless',
  'image',
  'attachment',
  'pdf',
  'embed',
];

const TIME_FILTERS: TimeFilter[] = ['all', 'day', 'week', 'month'];

// Stable empty list for the brief window before the search sessions are
// created (and during a StrictMode remount). A fresh `[]` per render would
// change referential identity every frame and needlessly re-run downstream
// memos/subscriptions.
const EMPTY_ITEMS: QuickSearchItem[] = [];
const SPLIT_HANDLE_WIDTH = 1;
const MIN_SEARCH_PANE_WIDTH = 360;
const MIN_PREVIEW_PANE_WIDTH = 360;
const DEFAULT_SEARCH_PANE_RATIO = 0.56;
const KEYBOARD_RESIZE_STEP = 32;
const ADVANCED_SEARCH_LOADING_TIMEOUT_MS = 5_000;
const SEARCH_PANE_RATIO_STORAGE_KEY =
  'streamlain:advanced-search:search-pane-ratio';

const SOURCE_LABEL_KEY: Record<SourceFilter, string> = {
  all: 'com.streamlain.quicksearch.advanced.source.all',
  doc: 'com.streamlain.quicksearch.sourceType.doc',
  text: 'com.streamlain.quicksearch.sourceType.text',
  database: 'com.streamlain.quicksearch.sourceType.database',
  mail: 'com.streamlain.quicksearch.sourceType.mail',
  edgeless: 'com.streamlain.quicksearch.sourceType.edgeless',
  image: 'com.streamlain.quicksearch.sourceType.image',
  attachment: 'com.streamlain.quicksearch.sourceType.attachment',
  pdf: 'com.streamlain.quicksearch.sourceType.pdf',
  embed: 'com.streamlain.quicksearch.sourceType.embed',
};

const TIME_LABEL_KEY: Record<TimeFilter, string> = {
  all: 'com.streamlain.quicksearch.advanced.time.all',
  day: 'com.streamlain.quicksearch.advanced.time.day',
  week: 'com.streamlain.quicksearch.advanced.time.week',
  month: 'com.streamlain.quicksearch.advanced.time.month',
};

function readStoredSearchPaneRatio() {
  if (typeof window === 'undefined') return null;
  try {
    const value = Number(
      window.localStorage.getItem(SEARCH_PANE_RATIO_STORAGE_KEY)
    );
    return Number.isFinite(value) && value > 0 && value < 1 ? value : null;
  } catch {
    return null;
  }
}

function writeStoredSearchPaneRatio(ratio: number) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      SEARCH_PANE_RATIO_STORAGE_KEY,
      ratio.toFixed(4)
    );
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }
}

function statusKey(status: I18nString) {
  if (typeof status === 'string') return status;
  return `${status.i18nKey}:${JSON.stringify(status.options ?? {})}`;
}

function dedupeStatuses(statuses: (I18nString | null)[]) {
  const seen = new Set<string>();
  return statuses.filter((status): status is I18nString => {
    if (!status) return false;
    const key = statusKey(status);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function AdvancedSearchDialog({
  initialQuery,
  onBackToNormal,
  onOpenChange,
  open,
}: {
  open: boolean;
  initialQuery: string;
  onOpenChange: (open: boolean) => void;
  onBackToNormal: (query: string) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {open ? (
        <AdvancedSearchDialogContent
          initialQuery={initialQuery}
          onOpenChange={onOpenChange}
          onBackToNormal={onBackToNormal}
        />
      ) : null}
    </Dialog.Root>
  );
}

function AdvancedSearchDialogContent({
  initialQuery,
  onBackToNormal,
  onOpenChange,
}: {
  initialQuery: string;
  onOpenChange: (open: boolean) => void;
  onBackToNormal: (query: string) => void;
}) {
  const framework = useFramework();
  const workbench = useService(WorkbenchService).workbench;
  const docsService = useService(DocsService);
  const docsSearchService = useService(DocsSearchService);
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const workspaceService = useService(WorkspaceService);
  const i18n = useI18n();
  // Own the search sessions for the lifetime of an open dialog. They are
  // created inside the effect (not in useState) on purpose: disposing a
  // DocsQuickSearchSession unsubscribes its RxJS query effect for good, and
  // React 18 StrictMode runs mount -> unmount -> mount in dev. With a useState
  // instance the throwaway first pass would dispose the shared instance, and
  // the committed mount would reuse a session whose query effect is already
  // dead, so every search would silently return zero hits. Creating fresh in
  // the effect means the disposed pass is thrown away and the live pair backs
  // the committed mount.
  const [sessions, setSessions] = useState<{
    docs: DocsQuickSearchSession;
    mail: MailQuickSearchSession;
    recent: RecentDocsQuickSearchSession;
  } | null>(null);
  const session = sessions?.docs ?? null;
  const mailSession = sessions?.mail ?? null;
  const recentSession = sessions?.recent ?? null;

  const [query, setQuery] = useState(initialQuery);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [selectedId, setSelectedId] = useState('');
  const [searchTimedOut, setSearchTimedOut] = useState(false);
  const selectedTargetKeyRef = useRef('');
  const pendingActionIndexRef = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [searchPaneRatio, setSearchPaneRatio] = useState<number | null>(
    readStoredSearchPaneRatio
  );
  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length > 0;
  const [previewPaneWasOpened, setPreviewPaneWasOpened] = useState(hasQuery);
  const showPreviewPane = hasQuery || previewPaneWasOpened;
  const connectivityStatus = useConnectivityStatus();
  const connectivitySearchDegraded = useUiOfflineSignal(
    hasQuery &&
      workspaceService.workspace.flavour !== 'local' &&
      connectivityStatus.offline
  );

  const docItems = useLiveData(session?.items$) ?? EMPTY_ITEMS;
  const mailItems = useLiveData(mailSession?.items$) ?? EMPTY_ITEMS;
  const rawItems = useMemo(
    () => (hasSearchText(query) ? [...docItems, ...mailItems] : docItems),
    [docItems, mailItems, query]
  );
  const recentItems = useLiveData(recentSession?.items$) ?? EMPTY_ITEMS;
  const docsLoading = useLiveData(session?.isLoading$) ?? false;
  const docsSettled = useLiveData(session?.isSettled$) ?? !hasQuery;
  const mailLoading = useLiveData(mailSession?.isLoading$) ?? false;
  const loading = docsLoading || mailLoading || (hasQuery && !docsSettled);
  const showSearchLoading = loading && !searchTimedOut;
  const docsError = useLiveData(session?.error$) ?? null;
  const mailError = useLiveData(mailSession?.error$) ?? null;
  const error = docsError || mailError;
  const docsStatus = useLiveData(session?.status$) ?? null;
  const mailStatus = useLiveData(mailSession?.status$) ?? null;
  const searchStatuses = useMemo(
    () =>
      dedupeStatuses([
        connectivitySearchDegraded ? SEARCH_DEGRADED_STATUS : null,
        docsStatus,
        mailStatus,
      ]),
    [connectivitySearchDegraded, docsStatus, mailStatus]
  );
  const degradedSearchStatus = searchStatuses[0] ?? null;
  const isSearchDegraded = degradedSearchStatus !== null;
  const indexerState = useLiveData(docsSearchService.indexerState$);

  useEffect(() => {
    const docs = framework.createEntity(DocsQuickSearchSession);
    const mail = framework.createEntity(MailQuickSearchSession);
    const recent = framework.createEntity(RecentDocsQuickSearchSession);
    setSessions({ docs, mail, recent });
    return () => {
      docs.dispose();
      mail.dispose();
      recent.dispose();
    };
  }, [framework]);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    session?.query(query);
    mailSession?.query(query);
  }, [mailSession, query, session]);

  useEffect(() => {
    if (hasQuery) setPreviewPaneWasOpened(true);
  }, [hasQuery]);

  const items = useMemo(() => {
    // Advanced Search presents a stable result set. The compact Quick Search
    // may publish its fast local snapshot immediately, but showing that here
    // as "2 of 2" before the remote merge supplies result three makes the
    // result count and selection visibly jump.
    if (hasQuery && !docsSettled) return EMPTY_ITEMS;
    const minTimestamp = minTimestampForTimeFilter(timeFilter);
    return rawItems.filter(item => {
      if (
        sourceFilter !== 'all' &&
        !getItemSourceTypes(item).includes(sourceFilter)
      ) {
        return false;
      }

      if (minTimestamp !== null) {
        const timestamp = getItemTimestamp(
          item,
          docsService,
          docDisplayMetaService
        );
        if (timestamp === null || timestamp < minTimestamp) return false;
      }

      return true;
    });
  }, [
    docDisplayMetaService,
    docsService,
    docsSettled,
    hasQuery,
    rawItems,
    sourceFilter,
    timeFilter,
  ]);

  useEffect(() => {
    if (!hasQuery || !loading || items.length > 0) {
      setSearchTimedOut(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setSearchTimedOut(true);
    }, ADVANCED_SEARCH_LOADING_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [hasQuery, items.length, loading, trimmedQuery]);

  const groups = useMemo(() => groupQuickSearchItems(items), [items]);

  // Flat, render-ordered list backing keyboard navigation. With a query it is
  // the grouped results in display order; without one it is the recent docs.
  const navItems = useMemo(
    () => (hasQuery ? groups.flatMap(group => group.items) : recentItems),
    [groups, hasQuery, recentItems]
  );

  const selectItem = useCallback((item: QuickSearchItem) => {
    selectedTargetKeyRef.current = getItemNavigationIdentity(item);
    setSelectedId(item.id);
  }, []);

  useEffect(() => {
    const selectedItem = navItems.find(item => item.id === selectedId);
    if (selectedItem) {
      pendingActionIndexRef.current = null;
      selectedTargetKeyRef.current = getItemNavigationIdentity(selectedItem);
      return;
    }

    const previousTargetKey = selectedTargetKeyRef.current;
    if (previousTargetKey) {
      const restoredItem = navItems.find(
        item => getItemNavigationIdentity(item) === previousTargetKey
      );
      if (restoredItem) {
        setSelectedId(restoredItem.id);
        return;
      }
    }

    if (selectedId && loading) return;

    const pendingIndex = pendingActionIndexRef.current;
    if (pendingIndex !== null) {
      pendingActionIndexRef.current = null;
      const candidate =
        navItems[Math.min(pendingIndex, navItems.length - 1)] ??
        navItems[pendingIndex - 1];
      if (candidate) {
        selectedTargetKeyRef.current = getItemNavigationIdentity(candidate);
        setSelectedId(candidate.id);
        return;
      }
    }

    const fallbackItem = navItems[0];
    selectedTargetKeyRef.current = fallbackItem
      ? getItemNavigationIdentity(fallbackItem)
      : '';
    setSelectedId(fallbackItem?.id ?? '');
  }, [loading, navItems, selectedId, trimmedQuery]);

  const selectedItem = useMemo(
    () => navItems.find(item => item.id === selectedId) ?? null,
    [navItems, selectedId]
  );
  const selectedDocRef = useMemo(
    () =>
      selectedItem
        ? getPreviewDocRef(selectedItem, trimmedQuery, payload =>
            resolveDocsSearchNavigation(payload, docsService)
          )
        : null,
    [docsService, selectedItem, trimmedQuery]
  );
  const selectedMailItem = isMailSearchItem(selectedItem) ? selectedItem : null;
  const activeView = useLiveData(workbench.activeView$);

  const indexerTotal = indexerState.total ?? 0;
  const indexerPending = indexerState.indexing ?? 0;
  const resultSummary = !hasQuery
    ? null
    : i18n.t({
        i18nKey: 'com.streamlain.quicksearch.advanced.results',
        options: {
          count: items.length,
          total: rawItems.length,
        },
      });
  const indexerSummary =
    indexerState.completed || indexerTotal === 0
      ? null
      : i18n.t({
          i18nKey: 'com.streamlain.quicksearch.advanced.indexing',
          options: {
            done: Math.max(0, indexerTotal - indexerPending),
            total: indexerTotal,
          },
        });
  const showsPlaceholderState = !hasQuery
    ? recentItems.length === 0
    : items.length === 0;

  const handleQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    },
    []
  );

  const focusSearchInput = useCallback(() => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
      searchInputRef.current?.select();
    });
  }, []);

  useEffect(() => {
    focusSearchInput();
  }, [focusSearchInput]);

  const handleOpen = useCallback(
    (item: QuickSearchItem | null, inSplit = false) => {
      if (!item) return;
      const at = inSplit ? ('beside' as const) : undefined;
      if (item.source === 'mail') {
        const payload = item.payload as MailPayload | undefined;
        if (payload?.route) {
          workbench.open(
            inSplit ? toFocusedMailMessageRoute(payload.route) : payload.route,
            at ? { at } : undefined
          );
          onOpenChange(false);
        }
        return;
      }
      const payload = item.payload as DocsPayload | undefined;
      if (!payload) return;
      const navigation = resolveDocsSearchNavigation(
        payload as DocsSearchNavigablePayload,
        docsService
      );
      if (!navigation) return;

      openDocsSearchNavigation(workbench, navigation, at ? { at } : undefined);
      navigationPanelRevealService.revealDoc(navigation.docId, {
        source: 'advanced-search',
        workspaceId: workspaceService.workspace.id,
      });
      onOpenChange(false);
    },
    [
      docsService,
      navigationPanelRevealService,
      onOpenChange,
      workbench,
      workspaceService.workspace.id,
    ]
  );

  const handleSubmit = useCallback(
    (item: QuickSearchItem | null, inSplit = false) => {
      if (!item) return;
      if (item.beforeSubmit && !item.beforeSubmit()) {
        const actionIndex = navItems.findIndex(
          navItem => navItem.id === item.id
        );
        pendingActionIndexRef.current = actionIndex >= 0 ? actionIndex : null;
        selectItem(item);
        return;
      }
      handleOpen(item, inSplit);
    },
    [handleOpen, navItems, selectItem]
  );

  const moveSelection = useCallback(
    (delta: number) => {
      if (navItems.length === 0) return;
      const index = navItems.findIndex(item => item.id === selectedId);
      const nextIndex =
        index < 0
          ? 0
          : Math.min(navItems.length - 1, Math.max(0, index + delta));
      selectItem(navItems[nextIndex]);
    },
    [navItems, selectItem, selectedId]
  );

  const collapseParent = useCallback(
    (item: QuickSearchItem) => {
      const index = navItems.findIndex(navItem => navItem.id === item.id);
      const indent = item.indent ?? 0;
      if (index === -1 || indent === 0) return false;

      for (let i = index - 1; i >= 0; i--) {
        const candidate = navItems[i];
        if ((candidate.indent ?? 0) < indent && candidate.expanded) {
          selectItem(candidate);
          if (candidate.beforeSubmit && !candidate.beforeSubmit()) {
            return true;
          }
          return false;
        }
      }
      return false;
    },
    [navItems, selectItem]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(-1);
      } else if (event.key === 'ArrowRight') {
        if (selectedItem?.expanded === false) {
          event.preventDefault();
          selectedItem.beforeSubmit?.();
        }
      } else if (event.key === 'ArrowLeft') {
        if (selectedItem?.expanded === true) {
          event.preventDefault();
          selectedItem.beforeSubmit?.();
        } else if (selectedItem && collapseParent(selectedItem)) {
          event.preventDefault();
        }
      } else if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(selectedItem, event.metaKey || event.ctrlKey);
      }
    },
    [collapseParent, handleSubmit, moveSelection, selectedItem]
  );

  const setClampedSearchPaneWidth = useCallback((nextWidth: number) => {
    const contentWidth = contentRef.current?.getBoundingClientRect().width ?? 0;
    const availableWidth = contentWidth - SPLIT_HANDLE_WIDTH;
    if (availableWidth <= 0) return;
    const maxWidth = Math.max(
      MIN_SEARCH_PANE_WIDTH,
      availableWidth - MIN_PREVIEW_PANE_WIDTH
    );
    const clampedWidth = Math.min(
      maxWidth,
      Math.max(MIN_SEARCH_PANE_WIDTH, nextWidth)
    );
    const nextRatio = clampedWidth / availableWidth;
    setSearchPaneRatio(nextRatio);
    writeStoredSearchPaneRatio(nextRatio);
  }, []);

  const startSplitResize = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!showPreviewPane || !contentRef.current) return;
      if (window.matchMedia('(max-width: 900px)').matches) return;
      event.preventDefault();
      event.stopPropagation();

      const rect = contentRef.current.getBoundingClientRect();
      const updateFromClientX = (clientX: number) => {
        setClampedSearchPaneWidth(clientX - rect.left);
      };
      updateFromClientX(event.clientX);

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handlePointerMove = (moveEvent: PointerEvent) => {
        moveEvent.preventDefault();
        updateFromClientX(moveEvent.clientX);
      };
      const stopResize = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', stopResize);
        window.removeEventListener('pointercancel', stopResize);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', stopResize);
      window.addEventListener('pointercancel', stopResize);
    },
    [setClampedSearchPaneWidth, showPreviewPane]
  );

  const handleSplitResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      event.stopPropagation();
      const contentWidth =
        contentRef.current?.getBoundingClientRect().width ?? 0;
      const availableWidth = Math.max(0, contentWidth - SPLIT_HANDLE_WIDTH);
      const currentWidth =
        availableWidth * (searchPaneRatio ?? DEFAULT_SEARCH_PANE_RATIO);
      setClampedSearchPaneWidth(
        currentWidth +
          (event.key === 'ArrowLeft'
            ? -KEYBOARD_RESIZE_STEP
            : KEYBOARD_RESIZE_STEP)
      );
    },
    [searchPaneRatio, setClampedSearchPaneWidth]
  );

  const contentStyle = useMemo(
    () =>
      showPreviewPane && searchPaneRatio !== null
        ? ({
            '--advanced-search-pane-width': `clamp(${MIN_SEARCH_PANE_WIDTH}px, calc(${(
              searchPaneRatio * 100
            ).toFixed(4)}% - ${(SPLIT_HANDLE_WIDTH * searchPaneRatio).toFixed(
              4
            )}px), calc(100% - ${
              SPLIT_HANDLE_WIDTH + MIN_PREVIEW_PANE_WIDTH
            }px))`,
          } as CSSProperties)
        : undefined,
    [searchPaneRatio, showPreviewPane]
  );

  return (
    <Dialog.Portal>
      <Dialog.Overlay className={styles.overlay} />
      <Dialog.Content
        ref={contentRef}
        className={clsx(
          styles.content,
          !showPreviewPane && styles.contentSingle
        )}
        data-has-preview={showPreviewPane}
        style={contentStyle}
        aria-describedby={undefined}
        onOpenAutoFocus={event => {
          event.preventDefault();
          focusSearchInput();
        }}
        onKeyDown={handleKeyDown}
      >
        <section className={styles.searchPane}>
          <header className={styles.header}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => onBackToNormal(query)}
              aria-label={i18n.t({
                i18nKey: 'com.streamlain.quicksearch.advanced.backToNormal',
              })}
            >
              <ArrowLeftSmallIcon />
            </button>
            <div className={styles.headerText}>
              <Dialog.Title className={styles.title}>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.title',
                })}
              </Dialog.Title>
              <div className={styles.headerSubtitle}>
                {resultSummary ??
                  i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.subtitle',
                  })}
              </div>
            </div>
            {indexerSummary ? (
              <span className={styles.headerStatus}>{indexerSummary}</span>
            ) : null}
            <Dialog.Close className={styles.closeButton}>
              <CloseIcon />
            </Dialog.Close>
          </header>
          <div className={styles.controls}>
            <label className={styles.searchInputRow}>
              <SearchIcon />
              <input
                ref={searchInputRef}
                className={styles.searchInput}
                autoFocus
                value={query}
                onChange={handleQueryChange}
                placeholder={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.placeholder',
                })}
              />
              {showSearchLoading ? <Loading size={18} /> : null}
            </label>
            <div className={styles.filterRow}>
              {SOURCE_FILTERS.map(filter => (
                <button
                  key={filter}
                  type="button"
                  className={styles.chip}
                  data-active={sourceFilter === filter ? 'true' : 'false'}
                  onClick={() => setSourceFilter(filter)}
                >
                  {i18n.t({ i18nKey: SOURCE_LABEL_KEY[filter] })}
                </button>
              ))}
              <span className={styles.filterDivider} />
              <Menu
                items={TIME_FILTERS.map(filter => (
                  <MenuItem
                    key={filter}
                    selected={timeFilter === filter}
                    onSelect={() => setTimeFilter(filter)}
                  >
                    {i18n.t({ i18nKey: TIME_LABEL_KEY[filter] })}
                  </MenuItem>
                ))}
              >
                <button
                  type="button"
                  className={clsx(styles.chip, styles.chipDropdown)}
                  data-active={timeFilter !== 'all' ? 'true' : 'false'}
                >
                  {i18n.t({ i18nKey: TIME_LABEL_KEY[timeFilter] })}
                  <ArrowDownSmallIcon />
                </button>
              </Menu>
            </div>
            <div className={styles.statusLine}>
              {error ? (
                <span className={styles.errorText}>
                  {UserFriendlyError.fromAny(error).message}
                </span>
              ) : null}
            </div>
          </div>
          <div
            className={clsx(
              styles.resultList,
              showsPlaceholderState && styles.resultListEmpty
            )}
          >
            {!hasQuery ? (
              recentItems.length > 0 ? (
                <>
                  <div className={styles.listHeading}>
                    {i18n.t({
                      i18nKey:
                        'com.streamlain.cmdk.affine.category.affine.recent',
                    })}
                  </div>
                  {recentItems.map(item => (
                    <AdvancedResultItem
                      key={item.id}
                      item={item}
                      selected={item.id === selectedId}
                      onSubmit={handleSubmit}
                      onSelect={selectItem}
                    />
                  ))}
                </>
              ) : (
                <EmptyState
                  title={i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.emptyQuery',
                  })}
                  description={i18n.t({
                    i18nKey:
                      'com.streamlain.quicksearch.advanced.emptyQueryDesc',
                  })}
                />
              )
            ) : items.length === 0 && showSearchLoading ? (
              <EmptyState
                icon={<Loading size={28} />}
                title={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.searching',
                })}
                description={i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.searchingDesc',
                })}
              />
            ) : items.length === 0 && !showSearchLoading ? (
              <EmptyState
                title={i18n.t({
                  i18nKey:
                    indexerSummary && rawItems.length === 0
                      ? 'com.streamlain.quicksearch.advanced.noResultsIndexing'
                      : 'com.streamlain.quicksearch.advanced.noResults',
                })}
                description={i18n.t({
                  i18nKey:
                    rawItems.length === 0
                      ? 'com.streamlain.quicksearch.advanced.noResultsDesc'
                      : 'com.streamlain.quicksearch.advanced.filteredNoResultsDesc',
                })}
              />
            ) : (
              groups.map(({ group, items }) => (
                <AdvancedResultGroup
                  key={group?.id ?? 'ungrouped'}
                  items={items}
                  selectedId={selectedId}
                  onSubmit={handleSubmit}
                  onSelect={selectItem}
                />
              ))
            )}
          </div>
          <div className={styles.searchFooter}>
            {isSearchDegraded ? (
              <span className={styles.statusPill}>
                {i18n.t(degradedSearchStatus)}
              </span>
            ) : null}
            <div className={styles.kbdHint}>
              <span>
                <kbd className={styles.kbd}>↑</kbd>
                <kbd className={styles.kbd}>↓</kbd>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.kbd.navigate',
                })}
              </span>
              <span>
                <kbd className={styles.kbd}>↵</kbd>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.kbd.open',
                })}
              </span>
              <span>
                <kbd className={styles.kbd}>esc</kbd>
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.advanced.kbd.close',
                })}
              </span>
            </div>
          </div>
        </section>
        {showPreviewPane ? (
          <div
            className={styles.splitResizeHandle}
            role="separator"
            aria-orientation="vertical"
            aria-label={i18n.t({
              i18nKey: 'com.streamlain.quicksearch.advanced.resizePreview',
            })}
            tabIndex={0}
            onKeyDown={handleSplitResizeKeyDown}
            onPointerDown={startSplitResize}
          />
        ) : null}
        {showPreviewPane ? (
          <aside className={styles.previewPane}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewKicker}>
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.preview',
                  })}
                </div>
                <div className={styles.previewTitle}>
                  {selectedItem
                    ? getPlainItemTitle(selectedItem, i18n.t)
                    : i18n.t({
                        i18nKey:
                          'com.streamlain.quicksearch.advanced.previewFallbackTitle',
                      })}
                </div>
              </div>
            </div>
            <div className={styles.previewBody}>
              {selectedMailItem ? (
                <div className={styles.previewCard}>
                  <AdvancedMailPreview item={selectedMailItem} />
                </div>
              ) : selectedDocRef ? (
                <div className={styles.previewCard}>
                  <FrameworkScope scope={activeView.scope}>
                    <DocPeekPreview
                      docRef={selectedDocRef}
                      hideReadOnlyPermissionBanner
                    />
                  </FrameworkScope>
                </div>
              ) : (
                <div className={styles.previewEmpty}>
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.previewEmpty',
                  })}
                </div>
              )}
            </div>
            {selectedItem ? (
              <div className={styles.previewFooter}>
                <Button
                  variant="secondary"
                  onClick={() => handleOpen(selectedItem, true)}
                >
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.openInSplit',
                  })}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleOpen(selectedItem)}
                >
                  {i18n.t({
                    i18nKey: 'com.streamlain.quicksearch.advanced.openResult',
                  })}
                </Button>
              </div>
            ) : null}
          </aside>
        ) : null}
      </Dialog.Content>
    </Dialog.Portal>
  );
}

function AdvancedMailPreview({
  item,
}: {
  item: QuickSearchItem<'mail', MailPayload> & { payload: MailPayload };
}) {
  const i18n = useI18n();
  const graphql = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const payload = item.payload;
  const messageId = payload?.messageId?.trim() || null;
  const body = useSWR<MailMessageBodyQuery['mailMessageBody']>(
    messageId ? ['advancedSearchMailPreview', workspaceId, messageId] : null,
    async () => {
      const response = await graphql.gql({
        query: mailMessageBodyQuery,
        variables: {
          workspaceId,
          messageId: messageId as string,
        },
      });
      return response.mailMessageBody;
    },
    { suspense: false }
  );
  const [, subtitle] = getItemTitleParts(item, i18n.t);

  return (
    <MailMessagePreview
      className={styles.advancedMailPreview}
      fitHtmlToWidth
      message={
        messageId
          ? {
              id: messageId,
              subject: payload?.subject,
              from: payload?.from,
              receivedAt: payload?.receivedAt,
              snippet: stripSearchHighlightTags(subtitle ?? ''),
              sizeBytes: body.data?.sizeBytes,
            }
          : null
      }
      body={body.data}
      isLoading={body.isLoading}
      error={body.error instanceof Error ? body.error.message : undefined}
    />
  );
}

function isMailSearchItem(
  item: QuickSearchItem | null
): item is QuickSearchItem<'mail', MailPayload> & { payload: MailPayload } {
  return item?.source === 'mail' && Boolean(item.payload);
}

function toFocusedMailMessageRoute(route: string) {
  const [pathAndSearch = '', hash = ''] = route.split('#', 2);
  const [pathname = '', search = ''] = pathAndSearch.split('?', 2);
  const params = new URLSearchParams(search);
  params.set('view', 'message');
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
}

function EmptyState({
  description,
  icon,
  title,
}: {
  title: ReactNode;
  description: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={styles.emptyState}>
      {icon ?? <SearchIcon />}
      <div className={styles.emptyTitle}>{title}</div>
      <div className={styles.emptyDescription}>{description}</div>
    </div>
  );
}

function AdvancedResultGroup({
  items,
  onSubmit,
  onSelect,
  selectedId,
}: {
  items: QuickSearchItem[];
  selectedId: string;
  onSelect: (item: QuickSearchItem) => void;
  onSubmit: (item: QuickSearchItem | null) => void;
}) {
  return (
    <>
      {items.map((item, index) => {
        const treeState = getTreeState(items, index);
        return (
          <AdvancedResultItem
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            treeState={treeState}
            onSubmit={onSubmit}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
}

function AdvancedResultItem({
  item,
  onSelect,
  onSubmit,
  selected,
  treeState,
}: {
  item: QuickSearchItem;
  selected: boolean;
  onSelect: (item: QuickSearchItem) => void;
  onSubmit: (item: QuickSearchItem | null) => void;
  treeState?: ResultTreeState;
}) {
  const i18n = useI18n();
  const ref = useRef<HTMLButtonElement>(null);
  const [title, subtitle] = getItemTitleParts(item, i18n.t);
  const Icon = typeof item.icon === 'function' ? item.icon : null;
  const iconNode = Icon ? (
    <Icon />
  ) : isRenderableIcon(item.icon) ? (
    item.icon
  ) : null;
  const indent = item.indent ?? 0;
  const expandable = item.expanded !== undefined;

  useEffect(() => {
    if (selected) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [selected]);

  return (
    <button
      ref={ref}
      type="button"
      className={styles.resultItem}
      data-selected={selected ? 'true' : 'false'}
      data-indent={indent > 0 ? indent : undefined}
      data-tree-last={treeState?.isLastSibling ? 'true' : undefined}
      data-outer-tree-last={treeState?.isOuterTreeLast ? 'true' : undefined}
      aria-expanded={expandable ? item.expanded : undefined}
      onClick={event => {
        if (expandable) {
          onSelect(item);
          // A double click emits two click events. Toggle only for the first so
          // the row cannot immediately collapse again.
          if (event.detail <= 1) onSubmit(item);
          return;
        }
        onSelect(item);
      }}
      onDoubleClick={expandable ? undefined : () => onSubmit(item)}
    >
      <span className={styles.resultChevronSlot}>
        {expandable ? (
          <span
            className={styles.resultChevron}
            data-expanded={item.expanded ? 'true' : 'false'}
            aria-hidden="true"
          >
            <ArrowDownSmallIcon />
          </span>
        ) : null}
      </span>
      <span className={styles.resultIcon}>{iconNode}</span>
      <span className={styles.resultText}>
        <span className={styles.resultTitle}>
          <span className={styles.resultTitleText}>
            <HighlightText text={title} start="<b>" end="</b>" />
          </span>
          {item.badge ? (
            <span className={styles.resultBadge}>{i18n.t(item.badge)}</span>
          ) : null}
        </span>
        {subtitle ? (
          <span className={styles.resultSubtitle}>
            <HighlightText text={subtitle} start="<b>" end="</b>" />
          </span>
        ) : null}
      </span>
      <span className={styles.resultMeta}>
        {item.timestamp ? i18nTime(new Date(item.timestamp)) : null}
      </span>
    </button>
  );
}

type ResultTreeState = {
  isLastSibling: boolean;
  isOuterTreeLast: boolean;
};

function getTreeState(
  items: QuickSearchItem[],
  index: number
): ResultTreeState {
  const item = items[index];
  const indent = item.indent ?? 0;
  let isLastSibling = false;
  if (indent > 0) {
    isLastSibling = true;
    for (let i = index + 1; i < items.length; i++) {
      const nextIndent = items[i].indent ?? 0;
      if (nextIndent > indent) continue;
      isLastSibling = nextIndent < indent;
      break;
    }
  }

  let isOuterTreeLast = false;
  if (indent === 2) {
    isOuterTreeLast = true;
    for (let i = index + 1; i < items.length; i++) {
      const nextIndent = items[i].indent ?? 0;
      if (nextIndent === 1) {
        isOuterTreeLast = false;
        break;
      }
      if (nextIndent === 0) break;
    }
  }

  return {
    isLastSibling,
    isOuterTreeLast,
  };
}

function isRenderableIcon(
  icon: QuickSearchItem['icon']
): icon is Exclude<ReactNode, undefined | null | boolean> {
  return (
    icon !== undefined &&
    icon !== null &&
    icon !== false &&
    icon !== true &&
    typeof icon !== 'function'
  );
}

function getItemTitle(
  item: QuickSearchItem,
  t: ReturnType<typeof useI18n>['t']
) {
  return getItemTitleParts(item, t)[0];
}

function getPlainItemTitle(
  item: QuickSearchItem,
  t: ReturnType<typeof useI18n>['t']
) {
  return stripSearchHighlightTags(getItemTitle(item, t));
}

function stripSearchHighlightTags(text: string) {
  return text.replaceAll('<b>', '').replaceAll('</b>', '');
}

function getItemTitleParts(
  item: QuickSearchItem,
  t: ReturnType<typeof useI18n>['t']
): [string, string | null] {
  if (isI18nString(item.label)) {
    return [t(item.label), null];
  }
  return [
    t(item.label.title),
    item.label.subTitle ? t(item.label.subTitle) : null,
  ];
}

function getPreviewDocRef(
  item: QuickSearchItem,
  query: string | undefined,
  resolveNavigation: (
    payload: DocsSearchNavigablePayload
  ) => DocsSearchNavigation | null
): DocReferenceInfo | null {
  if (item.source === 'mail') return null;

  const payload = item.payload as DocsPayload | undefined;
  if (!payload) return null;

  const navigation = resolveNavigation(payload as DocsSearchNavigablePayload);
  if (!navigation) return null;

  if (navigation.kind === 'attachment') {
    return {
      docId: navigation.docId,
      blockIds: [navigation.attachmentBlockId],
      mode: 'edgeless',
      pageIndex: navigation.pageIndex,
      query: navigation.query,
      highlightRects: navigation.highlightRects,
      requestId: item.id,
    };
  }

  const blockPayload = isDocsBlockPayload(payload) ? payload : null;
  const databaseId =
    blockPayload?.sourceType === 'database'
      ? (blockPayload.databaseId ?? blockPayload.blockId)
      : undefined;

  return {
    docId: navigation.docId,
    blockIds: navigation.blockIds,
    elementIds: navigation.elementIds,
    mode: navigation.mode,
    parentBlockId: blockPayload?.parentBlockId,
    parentFlavour: blockPayload?.parentFlavour,
    noteBlockId: blockPayload?.noteBlockId,
    databaseDocId: databaseId ? navigation.docId : undefined,
    databaseId,
    databaseRowId: blockPayload?.databaseRowId,
    query,
    requestId: item.id,
    fitToContent:
      !navigation.blockIds?.length && !navigation.elementIds?.length,
  };
}

function isDocsBlockPayload(payload: DocsPayload): payload is DocsBlockPayload {
  return !('kind' in payload);
}

function resolveDocsSearchNavigation(
  payload: DocsSearchNavigablePayload,
  docsService: DocsService
): DocsSearchNavigation | null {
  return buildDocsSearchNavigation(
    payload,
    createDocsSearchNavigationResolvers(docsService)
  );
}

function getItemNavigationIdentity(item: QuickSearchItem) {
  if (item.source === 'mail') {
    const payload = item.payload as MailPayload | undefined;
    return ['mail', payload?.messageId ?? '', payload?.route ?? item.id].join(
      '\u0000'
    );
  }

  const payload = item.payload as DocsPayload | undefined;
  if (!payload) return `item:${item.id}`;

  if ('kind' in payload) {
    return [
      'pdf',
      payload.kind,
      payload.docId,
      'attachmentBlockId' in payload ? payload.attachmentBlockId : '',
      'pageIndex' in payload ? payload.pageIndex : '',
    ].join('\u0000');
  }

  const isActionRow =
    item.beforeSubmit && !payload.blockId && !payload.elementId;
  if (isActionRow) return `action:${item.id}`;

  return [
    'doc',
    payload.sourceType ?? 'doc',
    payload.docId ?? '',
    payload.blockId ?? '',
    payload.elementId ?? '',
    payload.databaseId ?? '',
    payload.databaseRowId ?? '',
    payload.databaseColumnId ?? '',
  ].join('\u0000');
}

function getItemSourceTypes(item: QuickSearchItem) {
  if (item.source === 'mail') return ['mail'];

  const payload = item.payload as DocsPayload | undefined;
  return getDocsPayloadSourceTypes(payload);
}

function getItemTimestamp(
  item: QuickSearchItem,
  docsService: DocsService,
  docDisplayMetaService: DocDisplayMetaService
): number | null {
  if (typeof item.timestamp === 'number') return item.timestamp;
  const docId = (item.payload as { docId?: string } | undefined)?.docId;
  if (!docId) return null;
  const doc = docsService.list.doc$(docId).value;
  if (!doc) return null;
  const updatedDate = docDisplayMetaService.getDocDisplayMeta(doc).updatedDate;
  return updatedDate ? new Date(updatedDate).getTime() : null;
}

function minTimestampForTimeFilter(filter: TimeFilter) {
  if (filter === 'all') return null;
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  if (filter === 'day') return now - day;
  if (filter === 'week') return now - 7 * day;
  return now - 30 * day;
}

function hasSearchText(query: string) {
  return query.trim().length > 0;
}
